import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getDayDateRange, getTimelineEntryDate, isTimelineEntryVisibleOnDate } from './dateUtils';
import { dedupeTimelineEntries } from './timelineDeduping';
import { isDeletedTimelineEntry } from './deletionRules';
import { filterCareRecordsForViewer } from './recordVisibility';
import { normalizeDailyLogEntry } from '../../hooks/timeline/timelineSourceNormalizers';

const DAILY_LOGS_COLLECTION = 'dailyLogs';

const mapDailyLogSnapshotEntries = (snapshot) => snapshot.docs
  .map((doc) => {
    const data = doc.data();
    if (isDeletedTimelineEntry(data)) {
      return null;
    }

    return normalizeDailyLogEntry({
      id: doc.id,
      ...data,
      timestamp: data.timestamp || data.endAt || data.createdAt || new Date(),
    });
  })
  .filter(Boolean);

const buildDailyLogsBaseQuery = (childId, withOrdering = true) => {
  const constraints = [where('childId', '==', childId)];

  if (withOrdering) {
    constraints.push(orderBy('timestamp', 'desc'));
  }

  return query(collection(db, DAILY_LOGS_COLLECTION), ...constraints);
};

const runDailyLogsQueryWithFallback = async (childId, fallbackWarning, options = {}) => {
  try {
    const snapshot = await getDocs(buildDailyLogsBaseQuery(childId, true));
    return filterCareRecordsForViewer(mapDailyLogSnapshotEntries(snapshot), options.viewerRole);
  } catch (queryError) {
    if (queryError.message.includes('index')) {
      console.warn(fallbackWarning);
      const snapshot = await getDocs(buildDailyLogsBaseQuery(childId, false));
      return filterCareRecordsForViewer(mapDailyLogSnapshotEntries(snapshot), options.viewerRole);
    }

    throw queryError;
  }
};

const filterSelectedDayEntries = (entries = [], selectedDate, start, end) => {
  const filteredEntries = entries
    .filter((entry) => isTimelineEntryVisibleOnDate(entry, selectedDate, start, end))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  if (process.env.NODE_ENV !== 'production') {
    const sleepEntries = filteredEntries.filter((entry) => entry.category === 'sleep' || entry.type === 'sleep');
    if (sleepEntries.length > 0) {
      console.debug('[timeline] dailyLogs sleep query results', {
        selectedDate: selectedDate?.toDateString?.() || String(selectedDate),
        total: filteredEntries.length,
        sleepCount: sleepEntries.length,
        sleepEntries: sleepEntries.map((entry) => ({
          id: entry.id,
          timestamp: entry.timestamp,
          status: entry.status,
          sleepStatus: entry.sleepDetails?.status,
          sleepType: entry.sleepDetails?.type,
          startTime: entry.sleepDetails?.startTime || entry.startTime,
          endTime: entry.sleepDetails?.endTime || entry.endTime,
        })),
      });
    }
  }

  return dedupeTimelineEntries(filteredEntries);
};

const filterRangeEntries = (entries = [], startDate = null, endDate = null) => dedupeTimelineEntries(
  entries
    .filter((entry) => {
      const entryDate = getTimelineEntryDate(entry);
      if (!entryDate || Number.isNaN(entryDate.getTime())) {
        return false;
      }

      if (startDate && entryDate < startDate) {
        return false;
      }

      if (endDate && entryDate > endDate) {
        return false;
      }

      return true;
    })
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
);

/**
 * Get daily log entries for a specific child and date
 * @param {string} childId - Child ID
 * @param {Date} selectedDate - Date to fetch logs for
 * @returns {Promise<Array>} - Array of daily log entry objects
 */
export const getDailyLogEntries = async (childId, selectedDate, options = {}) => {
  try {
    const { start, end } = getDayDateRange(selectedDate);
    const mappedEntries = await runDailyLogsQueryWithFallback(
      childId,
      '🔥 Index missing for daily logs, using fallback query',
      options
    );
    return filterSelectedDayEntries(mappedEntries, selectedDate, start, end);
  } catch (error) {
    console.error('Error fetching daily log entries:', error);
    return [];
  }
};

export const subscribeToDailyLogEntries = (
  childId,
  selectedDate,
  options = {},
  onEntries = () => {},
  onError = () => {}
) => {
  if (!childId || !selectedDate) {
    onEntries([]);
    return () => {};
  }

  const { start, end } = getDayDateRange(selectedDate);
  let previousSnapshotSignature = '';
  return onSnapshot(
    buildDailyLogsBaseQuery(childId, false),
    (snapshot) => {
      const snapshotSignature = JSON.stringify(snapshot.docs.map((doc) => [doc.id, doc.data()]));
      if (snapshotSignature === previousSnapshotSignature) {
        return;
      }
      previousSnapshotSignature = snapshotSignature;

      const mappedEntries = filterCareRecordsForViewer(
        mapDailyLogSnapshotEntries(snapshot),
        options.viewerRole
      );
      onEntries(filterSelectedDayEntries(mappedEntries, selectedDate, start, end));
    },
    onError
  );
};

export const getDailyLogEntriesInRange = async (childId, startDate = null, endDate = null, options = {}) => {
  try {
    const mappedEntries = await runDailyLogsQueryWithFallback(
      childId,
      '🔥 Index missing for daily logs range query, using fallback query',
      options
    );
    return filterRangeEntries(mappedEntries, startDate, endDate);
  } catch (error) {
    console.error('Error fetching daily log entries in range:', error);
    return [];
  }
};
